import { ReactNode } from "react";

import {
  LookupIcon,
  MapAreaIcon,
  ProfilingIcon,
  StorageIcon
} from "@/assets/icons";
import { Divider } from "@/components/ui/Divider";

import { twMerge } from "tailwind-merge";

interface IconTextProps {
  icon: ReactNode;
  text: string;
}

const IconText = ({ icon, text }: IconTextProps) => {
  return (
    <div className="w-1/3 flex items-center gap-2">
      {icon}
      <span className="text-[10px]">{text}</span>
    </div>
  );
};

interface ProjectCardProps {
  className?: string;
  dataTestid?: string;
  title: string;
  subtitle?: ReactNode;
  description?: string;
  datasetCount: number;
  areaInputType: string;
  outputType: string;
  isProfiling: boolean;
  isSelected?: boolean;
  footer?: ReactNode;
  onClick?: () => void;
}

export const ProjectCard = ({
  className,
  dataTestid,
  title,
  subtitle,
  description,
  datasetCount,
  areaInputType,
  outputType,
  isProfiling,
  isSelected = false,
  footer,
  onClick
}: ProjectCardProps) => {
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      data-testid={dataTestid}
      onClick={onClick}
      className={twMerge(
        "w-full text-left rounded-lg h-auto hover:bg-teal-50 hover:border-teal-200",
        className,
        isSelected
          ? "border-teal-200 bg-teal-50"
          : "border-gainsboro hover:bg-teal-50 hover:border-teal-200"
      )}
    >
      <div className="py-2 px-1 overflow-hidden">
        <div className="flex flex-col gap-1">
          {subtitle && (
            <p className="text-[10px] text-txtPlaceholder">{subtitle}</p>
          )}

          <p
            className={twMerge(
              "font-bold line-clamp-2",
              isSelected ? "text-teal-500" : "text-txtHeading",
              "group-hover:text-teal-500"
            )}
            title={title}
          >
            {title}
          </p>

          {description && (
            <p className="truncate text-[10px]" title={description}>
              {description}
            </p>
          )}
        </div>

        <Divider
          className="bg-white-smoke w-full h-[1px] my-2"
          lineClassName={twMerge(
            "group-hover:border-teal-200",
            isSelected && "border-teal-200"
          )}
        />

        <div className="flex">
          <IconText
            icon={<StorageIcon width={16} className="fill-purple-500" />}
            text={`${datasetCount} dataset${datasetCount <= 1 ? "" : "s"}`}
          />

          <IconText icon={<MapAreaIcon width={16} />} text={areaInputType} />

          <IconText
            icon={
              isProfiling ? (
                <ProfilingIcon width={14} className="fill-teal-500" />
              ) : (
                <LookupIcon width={14} className="fill-teal-500" />
              )
            }
            text={outputType}
          />
        </div>

        {footer}
      </div>
    </Wrapper>
  );
};
